import { InfiniteData } from 'react-query/types/core/types';
import { dedupe, insertAllToList, replaceInList } from '../utils/ArrayUtils';

export const COMMUNITIES_KEY = 'COMMUNITIES';
export const REQUESTS_KEY = 'REQUESTS';
export const CORRESPONDENCES_KEY = 'CORRESPONDENCES';
export const POSTS_QUERY_KEY = 'posts';
export const COMMENTS_QUERY_KEY = 'comments';
export const MEMBERS_QUERY_KEY = 'members';
export const PROPERTIES_QUERY_KEY = 'properties';
export const CALENDARS_QUERY_KEY = 'calendars';
export const CALENDAR_EVENTS_QUERY_KEY = 'calendarEvents';
const IN_APP_MESSAGES_KEY = 'IN_APP_MESSAGES';
const NOTIFICATION_PREFERENCE_KEY = 'NOTIFICATION_PREFERENCE';
export const FOLDER_DETAIL_KEY = 'FOLDER_DETAIL';
export const FOLDER_TREE_KEY = 'FOLDER_TREE';
const PROFILE_KEY = 'PROFILE';
const ID_TOKEN_RESULT_KEY = 'ID_TOKEN_RESULT';
const MEMBER_CUSTOM_ATTRIBUTE_DEFINITIONS_KEY = 'MEMBER_CUSTOM_ATTRIBUTE_DEFINITIONS';
export const LIST = 'LIST';
export const DETAIL = 'DETAIL';
export const INFINITE = 'INFINITE';

interface Page<T> {
  content: T[];
}

export const getInfinitePagesContent = <T, P extends Page<T>>(
  infiniteData: InfiniteData<P> | undefined,
  keyExtractor: (item: T) => string | number
): T[] => {
  if (!infiniteData?.pages) {
    return [];
  }
  return dedupe(
    infiniteData!.pages.flatMap((it) => it.content),
    keyExtractor
  );
};

export const insertToInfinitePages = <T, P extends Page<T>>(
  infiniteData: InfiniteData<P> | undefined,
  newItem: T,
  end?: boolean
): InfiniteData<P> => {
  return insertAllToInfinitePages(infiniteData, [newItem], end);
};

export const insertAllToInfinitePages = <T, P extends Page<T>>(
  infiniteData: InfiniteData<P> | undefined,
  newItems: T[],
  end?: boolean
): InfiniteData<P> => {
  const safePages = infiniteData?.pages ?? [];
  let newPages: P[];
  if (safePages.length === 0) {
    newPages = [
      // @ts-ignore
      {
        content: insertAllToList([], newItems, true),
      },
    ];
  } else if (end) {
    const other = safePages.slice(0, safePages.length - 1);
    const last = safePages[safePages.length - 1];
    newPages = [
      ...other,
      {
        ...last,
        content: insertAllToList(last.content ?? [], newItems, true),
      },
    ];
  } else {
    const [first, ...other] = infiniteData?.pages ?? [];
    newPages = [
      {
        ...first,
        content: insertAllToList(first.content ?? [], newItems),
      },
      ...other,
    ];
  }

  return {
    pageParams: [0],
    ...infiniteData,
    pages: newPages,
  };
};

export const replaceInInfinitePages = <T, P extends Page<T>>(
  infiniteData: InfiniteData<P> | undefined,
  predicate: (oldItem: T) => boolean,
  transform: (oldItem: T) => T
): InfiniteData<P> | undefined => {
  if (!infiniteData) {
    return infiniteData;
  }

  const newPages = infiniteData.pages.map((page) => {
    return {
      ...page,
      content: replaceInList(page.content, predicate, transform),
    };
  });
  return {
    ...infiniteData,
    pages: newPages,
  };
};

export const requestsQueryKey = (communityId: number) => [COMMUNITIES_KEY, communityId, REQUESTS_KEY];
export const requestCorrespondencesQueryKey = (communityId: number, requestId: number) => [
  COMMUNITIES_KEY,
  communityId,
  REQUESTS_KEY,
  requestId,
  CORRESPONDENCES_KEY,
];
export const postsQueryKey = (communityId: number) => [COMMUNITIES_KEY, communityId, POSTS_QUERY_KEY];
export const postQueryKey = (communityId: number, postId: number) => [
  COMMUNITIES_KEY,
  communityId,
  POSTS_QUERY_KEY,
  postId,
];
export const postCommentsQueryKey = (communityId: number, postId: number) => [
  COMMUNITIES_KEY,
  communityId,
  POSTS_QUERY_KEY,
  postId,
  COMMENTS_QUERY_KEY,
];
export const inAppMessagesQueryKey = (communityId: number) => [COMMUNITIES_KEY, communityId, IN_APP_MESSAGES_KEY];
export const notificationPreferenceQueryKey = (communityId: number) => [
  COMMUNITIES_KEY,
  communityId,
  NOTIFICATION_PREFERENCE_KEY,
];
export const profileQueryKey = () => [PROFILE_KEY];
export const idTokenResultKey = () => [ID_TOKEN_RESULT_KEY];

export const memberCustomAttributeDefinitionsQueryKey = (communityId: number) => [
  COMMUNITIES_KEY,
  communityId,
  MEMBER_CUSTOM_ATTRIBUTE_DEFINITIONS_KEY,
];
